import type { CSSProperties, ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type JarvisFieldSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | (string & {});

export const JARVIS_INPUT_SIZE_CLASS: Record<string, string> = {
  xs: 'h-7 px-2 text-xs md:text-xs',
  sm: 'h-8 px-2.5 text-sm md:text-sm',
  md: 'h-10 px-3 text-sm md:text-sm',
  lg: 'h-11 px-4 text-base md:text-base',
  xl: 'h-12 px-4 text-base md:text-base',
};

/** Mantine-style `styles` prop — only the flat `input` object is applied. */
export function extractInputStyle(styles: unknown): CSSProperties | undefined {
  if (!styles || typeof styles === 'function') return undefined;
  const input = (styles as { input?: unknown }).input;
  if (!input || typeof input !== 'object') return undefined;

  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    // Skip Mantine pseudo / nested selectors (e.g. '&:focus')
    if (key.startsWith('&') || key.startsWith(':') || key.startsWith('@')) continue;
    result[key] = value;
  }
  return result as CSSProperties;
}

export function getJarvisControlStyle(
  themeColors: {
    inputBackground?: string;
    inputText?: string;
    inputBorder?: string;
    inputPlaceholder?: string;
    primary?: string;
    accent?: string;
    border?: string;
    error?: string;
  },
  options: {
    hasError?: boolean;
    styles?: unknown;
    style?: CSSProperties;
  } = {}
): CSSProperties {
  const fromStyles = extractInputStyle(options.styles);
  // Prefer accent/border over bright primary fills (dark/jarvis buttons are near-white).
  const softRing = themeColors.accent ?? themeColors.border ?? themeColors.primary;
  return {
    backgroundColor: themeColors.inputBackground,
    color: themeColors.inputText,
    borderColor: options.hasError ? themeColors.error ?? '#EF4444' : themeColors.inputBorder,
    // Used by ui/input + ui/textarea placeholder / focus utilities
    ['--jarvis-input-placeholder' as string]: themeColors.inputPlaceholder,
    ['--jarvis-input-ring' as string]: softRing,
    ...fromStyles,
    ...options.style,
  };
}

/** Soft focus: thin ring + ~40% opacity — visible a11y without stark white outlines. */
export const jarvisSoftFocusClassName = [
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-offset-0',
  'focus-visible:ring-[color:color-mix(in_srgb,var(--jarvis-input-ring,hsl(var(--ring)))_40%,transparent)]',
  'focus-visible:border-[color:color-mix(in_srgb,var(--jarvis-input-ring,hsl(var(--ring)))_55%,transparent)]',
].join(' ');

export function jarvisControlClassName(size: JarvisFieldSize | undefined, className?: string) {
  return cn(
    JARVIS_INPUT_SIZE_CLASS[size ?? 'sm'] ?? JARVIS_INPUT_SIZE_CLASS.sm,
    // Match shadcn Input tokens; theme colors still flow via getJarvisControlStyle CSS vars.
    'rounded-md border border-input bg-background text-foreground shadow-none',
    'placeholder:text-[color:var(--jarvis-input-placeholder,hsl(var(--muted-foreground)))]',
    jarvisSoftFocusClassName,
    className
  );
}

/**
 * Renders a field label with at most one required asterisk.
 * Returns null when there is no visible label text — callers that put
 * JarvisInputLabel outside the control (HeaderForm + AsyncSelect label="")
 * must not get a phantom empty <label>*</label> above the Combobox.
 */
export function renderFieldLabel(options: {
  id?: string;
  label?: ReactNode;
  required?: boolean;
  withAsterisk?: boolean;
  textColor?: string;
}) {
  const { id, label, required, withAsterisk, textColor } = options;
  if (label == null || label === false) return null;
  if (typeof label === 'string' && label.trim() === '') return null;
  // Asterisk only when there is a real label to attach it to.
  const showAsterisk = Boolean(required || withAsterisk);
  return (
    <label htmlFor={id} className="mb-1 block text-sm font-medium" style={{ color: textColor }}>
      {label}
      {showAsterisk ? <span className="ml-1 text-destructive">*</span> : null}
    </label>
  );
}

export function renderFieldDescription(description: ReactNode | undefined, textColor?: string) {
  if (description == null || description === false) return null;
  return (
    <p className="mt-1 text-xs" style={{ color: textColor }}>
      {description}
    </p>
  );
}

export function renderFieldError(error: ReactNode | undefined, errorColor?: string) {
  if (error == null || error === false || error === true) return null;
  return (
    <p className="mt-1 text-xs" role="alert" style={{ color: errorColor ?? '#EF4444' }}>
      {error}
    </p>
  );
}
